import { Form, FormikProvider, useFormik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from 'yup';
import * as Sentry from '@sentry/react';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Link,
    Stack,
    Checkbox,
    TextField,
    IconButton,
    InputAdornment,
  } from '@mui/material';
import { BackButton } from "../../back-button";
import { LoadingButton } from "@mui/lab";
import { useGenericMutation } from "../../../services/react-query/generic.mutation";
import { SystemUserTypes } from "../../../types";
import { handleApiError, queryClient } from "../../../services/react-query/react-query.client";
import { toast } from "react-toastify";
import { AuthTypes } from "../../../types/auth.types";
import { store, resetAllStores } from "../../../store";
import Zendesk from "../../../utils/zendesk";
import { startAuthenticatedReplay } from "../../../services/sentry-replay.service";
import { safeRedirectPath } from "../../../helpers/safeRedirectPath";

export const StepLoginForm = () => {
    const {
        mutateAsync: checkEmailAsync,
        isLoading,
    } = useGenericMutation<{ email: string }, SystemUserTypes.Model>('authCheckEmail', 'auth/check-email', 'post');

    const {
        mutateAsync: loginAsync,
        isLoading: loginLoading,
    } = useGenericMutation<{ email: string, password: string }, AuthTypes.LoginResult>('authLogin', 'auth/login', 'post');

    const navigate = useNavigate();
    const location = useLocation();
    const [showPassword, setShowPassword] = useState(false);

    const [step, setStep] = useState(0);

    const stepOneSchema = Yup.object({
        email: Yup
            .string()
            .email('Incorrect email')
            .required('Type your email'),
    });

    const stepTwoSchema = Yup.object({
        password: Yup
            .string()
            .required('Type your password')
    })

    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
        },
        validationSchema: step === 0 ? stepOneSchema : stepTwoSchema,
        onSubmit: async (values) => {
            if (step === 0) {
                await checkEmailAsync({ email: values.email }, {
                    onSuccess: (data) => {
                        const { result } = data;    

                        if (
                            result?.customer?.ssoConfig?.active && result?.ssoConfig?.active) {
                            const { clientId, ssoUrl } = result.customer.ssoConfig;
                            window.location.replace(`${ssoUrl}/oauth2/authorize?client_id=${clientId}&response_type=token&scope=openid%20email&redirect_uri=https://aware.pomsafe.com?`);
                        } else {
                            setStep(1);
                        }
                        
                    },
                    onError: (error) => {
                        const msg = handleApiError(error);
    
                        toast(msg, {
                            autoClose: 1500,
                            type: 'error',
                        });
                    }
                });
                return;
            }

           await loginAsync(values, {
                onSuccess: async (response) => {
                    if (!response.isValid) {
                        const msg = handleApiError(response.message);

                        toast(msg, {
                            autoClose: 1500,
                            type: 'error',
                        });
                        return;
                    }

                    // A new identity is taking over this tab: drop every
                    // org-scoped store and react-query cache the previous
                    // session left behind (same convention as the masquerade
                    // flow). Without this, GET /v2/settings/me serves the
                    // PREVIOUS org's cached settings to the new user for its
                    // stale window — e.g. a POM Master logging in after a
                    // standalone-org session was shown the standalone terms
                    // gate. auth store is preserved by RESET_ALL_STORES.
                    resetAllStores();
                    queryClient.clear();

                    if (response.result.subscription) {
                        store.dispatch.customer.setSelectedSubscription(
                            response.result.subscription
                        );
                    }

                    store.dispatch.auth.auth(response?.result?.user, response?.result?.token);
                    Sentry.setUser({ id: response?.result?.user?.id, email: response?.result?.user?.email, username: response?.result?.user?.fullname });
                    startAuthenticatedReplay();

                    Zendesk.authenticateUser(store.getState().auth.authUser);

                    // Feature-flag visibility gates read useEffectiveSettings
                    // (GET /v2/settings/me); no legacy settings fetch needed here.

                    toast(
                        'Login successfully!',
                        {
                            autoClose: 1500,
                            type: 'success',
                        },
                    );

                    // Deep link into a specific chat (ClickUp 868mb93wj):
                    // RedirectToLoginPreservingPath (routes/index.tsx) stashes
                    // the originally-requested path+search here before
                    // bouncing to /login — return there instead of the
                    // default '/' when present.
                    const from = safeRedirectPath((location.state as { from?: string } | null)?.from);
                    navigate(from || '/', { replace: true });
                },
                onError: (error) => {
                    const msg = handleApiError(error);

                    toast(msg, {
                        autoClose: 1500,
                        type: 'error',
                    });
                }
           });
           
        }
    });

    const handleShowPassword = () => {
        setShowPassword((show) => !show);
    };

    const { 
        errors,
        touched,
        handleSubmit,
        getFieldProps,
    } = formik;

    return (
        <FormikProvider value={formik}>
            <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
                <Stack spacing={3} sx={{ mt: -4 }}>
                    {step === 1 &&
                        <BackButton onClick={() => setStep(0)} />
                    }
                    {step === 0 &&
                        <TextField
                            fullWidth
                            autoComplete="username"
                            type="email"
                            label="Email Address"
                            {...getFieldProps('email')}
                            error={Boolean(touched.email && errors.email)}
                            helperText={touched.email && errors.email}
                        />
                    }
                    {step === 1 &&
                        <TextField
                            fullWidth
                            autoComplete="current-password"
                            type={showPassword ? 'text' : 'password'}
                            label="Password"
                            {...getFieldProps('password')}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleShowPassword} edge="end">
                                            <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            error={Boolean(touched.password && errors.password)}
                            helperText={touched.password && errors.password}
                        />
                    }

                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
                        <Link component={RouterLink} variant="subtitle2" to="/recover-password">
                            Forgot password?
                        </Link>
                        <LoadingButton
                            
                            size="large"
                            type="submit"
                            variant="contained"
                            loading={loginLoading || isLoading}
                        >
                            CONTINUE
                        </LoadingButton>
                    </Stack>
                    
                </Stack>
            </Form>
        </FormikProvider>
    )
}